import { useRef } from "react"

import { useValidateSastGuidance } from "@/api/useSettings"
import { Alert, Button, Card, Flex, Select } from "@/atoms"
import { useLogger } from "@/hooks/useLogger"
import { useModal } from "@/hooks/useModal"
import { useTranslation } from "@/hooks/useTranslation"
import { CloseCircleOutlined, FileTextOutlined, KaiAction } from "@/icons"
import { Form, RESOURCES, Rules } from "@/organisms/Form"
import { useGetOptionsSastGuidance } from "@/pages/SettingsPage/application/guidance/useGuidance"
import { FIELD_NAMES as SELECTOR_FIELD_NAMES } from "@/pages/SettingsPage/application/guidance/useGuidance.selector"
import { SECTION_IDS } from "@/pages/SettingsPage/components/pages/GuidancePage/helpers"
import { FIELD_NAMES } from "../GuidanceForm/helpers"
import { ReviewKaiGuidanceModal } from "../ReviewKaiGuidanceModal"
import { TextArea } from "./TextArea"

import styles from "./GuidanceFormSection.module.scss"

import type { GetRef, TextArea as TextAreaAtom } from "@/atoms"
import type { GuidanceFormSectionProps } from "."

export const GuidanceFormSection = (props: GuidanceFormSectionProps) => {
  const { impactCtx } = props
  const { form, custom, entityId, isNewEntity, message } = Form.useForm()
  const optionsCtx = useGetOptionsSastGuidance()
  const validateCtx = useValidateSastGuidance()

  const { visible, show, hide } = useModal()
  const { t } = useTranslation()
  const { t: tWriteGuidance } = useTranslation("translation", {
    keyPrefix: "settings.kai-guidance.form.writeGuidance"
  })
  const guidanceText = Form.useWatch(FIELD_NAMES.guidanceText, form)
  const canonicalText = Form.useWatch(FIELD_NAMES.canonicalText, form)
  const sessionId = Form.useWatch(FIELD_NAMES.sessionId, form)
  const refTextArea = useRef<GetRef<typeof TextAreaAtom>>(null)
  const { logger, EVENTS } = useLogger()

  const options = optionsCtx?.data

  const { isPending } = validateCtx
  const delimiter = "---&^&^&---"
  const errors = form
    .getFieldError(FIELD_NAMES.guidanceText)
    .map((item) => item.split(delimiter))
  const isTouched = form.isFieldTouched(FIELD_NAMES.guidanceText)
  const disabled = impactCtx.isPending
  const onOk = async (overrides?: {
    sessionId?: string
    canonicalText?: string
  }) => {
    try {
      const {
        sessionId: currentSessionId,
        canonicalText: currentCanonicalText
      } = overrides || {}
      const sessionId = currentSessionId ?? validateCtx.data?.data?.sessionId

      if (!sessionId) throw new Error(tWriteGuidance("errors.missingSessionId"))

      form.setFieldsValue({
        [FIELD_NAMES.guidanceTestImpact]: true,
        [FIELD_NAMES.guidanceText]: currentCanonicalText ?? canonicalText
      })
      hide()
      logger.info(EVENTS.ANALYTIC_EVENTS.GUIDANCE.REVISED_TEXT_ACCEPTED, {
        category: "sast",
        noChanges: !!currentSessionId
      })

      await impactCtx.mutateAsync({
        sessionId,
        sampleSize: 20,
        showMore: false
      })
    } catch (error: any) {
      message.instance.error(error?.message || "Something went wrong")
    }
  }

  return (
    <Form.Section.Template
      Icon={FileTextOutlined}
      title={tWriteGuidance("title")}
      id={SECTION_IDS.guidanceWrite}
    >
      <Form.Item noStyle name={FIELD_NAMES.sessionId} />
      <Form.Item
        required
        className={styles.label}
        name={FIELD_NAMES.cweIds}
        label={tWriteGuidance("cweIds.label")}
        rules={[Rules.getRequiredFieldRule(t)]}
      >
        <Select
          allowClear
          showSearch
          disabled={disabled}
          mode="multiple"
          placeholder={tWriteGuidance("cweIds.placeholder")}
          options={options?.[SELECTOR_FIELD_NAMES.cwes]}
        />
      </Form.Item>
      <Form.Item
        className={styles.label}
        name={FIELD_NAMES.detectionRulesIds}
        label={tWriteGuidance("detectionRulesIds.label")}
      >
        <Select
          disabled={disabled}
          mode="multiple"
          placeholder={tWriteGuidance("detectionRulesIds.placeholder")}
          options={options?.[SELECTOR_FIELD_NAMES.ruleIds]}
        />
      </Form.Item>
      <Form.Item
        required
        className={styles.controlHidden}
        name={FIELD_NAMES.canonicalText}
        label={
          <span className={styles.label}>
            {tWriteGuidance("knowledge.label")}
          </span>
        }
      />
      <Card className={styles.card}>
        <Flex vertical gap={32}>
          <Form.Item
            className={styles.label}
            name={FIELD_NAMES.guidanceText}
            rules={[Rules.getRequiredFieldRule(t)]}
          >
            <TextArea
              disabled={disabled}
              refTextArea={refTextArea}
              isPending={isPending}
              t={tWriteGuidance}
            />
          </Form.Item>
          <Flex vertical gap={16}>
            {isTouched && errors?.length > 0 && (
              <Alert
                showIcon
                icon={<CloseCircleOutlined />}
                className={styles.alert}
                type="error"
                title={errors[0]?.[0]}
                description={errors[0]?.[1]}
              />
            )}
            <Button
              type="primary"
              className={styles.refineButton}
              icon={<KaiAction />}
              disabled={!guidanceText || isPending || !!sessionId}
              loading={isPending}
              onClick={async () => {
                try {
                  await form.validateFields()

                  const values = form.getFieldsValue()
                  const { resources, guidanceText } = values
                  const specific_resources = values[RESOURCES.SPECIFIC]
                  const payload = {
                    sessionId: validateCtx.data?.data?.sessionId,
                    sourceGuidanceId: !isNewEntity ? entityId : undefined,
                    guidanceText: guidanceText,
                    filters: {
                      scope: {} as Record<string, string[]>,
                      cwes: values[FIELD_NAMES.cweIds] || [],
                      ruleIds: values[FIELD_NAMES.detectionRulesIds] || []
                    }
                  }

                  if (
                    resources === RESOURCES.SPECIFIC &&
                    specific_resources?.length > 0
                  ) {
                    const { type, value } = specific_resources[0] as {
                      type: string
                      value: string[]
                    }

                    payload.filters.scope[type] = value
                  }

                  if (!payload.sessionId) delete payload.sessionId
                  if (!payload.sourceGuidanceId) delete payload.sourceGuidanceId

                  const { data } = await validateCtx.mutateAsync(payload)
                  const { status, errors, canonicalText } = data || {}

                  if (status === "ERROR") {
                    form.setFields([
                      {
                        name: FIELD_NAMES.guidanceText,
                        touched: true,
                        errors: errors.map(
                          (item) =>
                            `${item.message}${delimiter}${item.suggestedRewrite}`
                        )
                      }
                    ])
                    logger.info(
                      EVENTS.ANALYTIC_EVENTS.GUIDANCE
                        .VALIDATION_ERROR_DISPLAYED,
                      { category: "sast" }
                    )
                  }

                  if (!errors.length && canonicalText.length) {
                    const sessionId = data?.sessionId

                    form.setFields([
                      { name: FIELD_NAMES.guidanceText, touched: false },
                      { name: FIELD_NAMES.canonicalText, value: canonicalText },
                      { name: FIELD_NAMES.sessionId, value: sessionId }
                    ])
                    custom.setIsDirty(true)

                    if (canonicalText.trim() !== guidanceText.trim()) show()
                    else onOk({ sessionId, canonicalText })
                  }
                } catch (error: any) {
                  if (error.errorFields?.length)
                    form.scrollToField(error.errorFields[0].name, {
                      block: "center",
                      scrollMode: "if-needed"
                    })
                  else
                    form.setFields([
                      { name: FIELD_NAMES.guidanceText, touched: true },
                      {
                        name: FIELD_NAMES.guidanceText,
                        errors: [tWriteGuidance("errors.validation")]
                      }
                    ])
                }
              }}
            >
              {isPending
                ? t("general.validating")
                : tWriteGuidance("refineButton")}
            </Button>
          </Flex>
        </Flex>
      </Card>
      <ReviewKaiGuidanceModal
        guidanceText={guidanceText}
        canonicalText={canonicalText}
        open={visible}
        onOk={() => onOk()}
        onCancel={() => {
          form.setFieldValue(FIELD_NAMES.sessionId, undefined)
          hide()
          logger.info(EVENTS.ANALYTIC_EVENTS.GUIDANCE.REVISED_TEXT_REJECTED, {
            category: "sast"
          })
        }}
        afterOpenChange={(open) => {
          if (!open) {
            refTextArea.current?.focus({ cursor: "end" })
          }
        }}
      />
    </Form.Section.Template>
  )
}
